/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Breadcrumbs, Button, Content } from '@orobix/orobix-fe-components'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'

import useDriverStyle from '../../components/Driver/driver.styles'
import PageHeader from '../../components/PageHeader/PageHeader'
import { SettingsContext } from '../../components/Settings'
import useUpdateSettings from '../../components/Settings/useUpdateSettings'
import { useDispatch, useStore } from '../../store/store'
import { fetchProduct, fetchProject } from '../../store/storeActions'
import { vars } from '../../utils/appConfig'
import { locations } from '../../utils/locations'
import DatasetDetails from './DatasetDetails'
import DatasetsAside from './DatasetsAside'
import DatasetsList, { Dataset } from './DatasetsList'
import useStyles from './DatasetsRoot.styles'
import getDriver from './Driver'

interface RouteParams {
  productId: string
  projectId: string
}

interface LocationState {
  datasetId: number
}

const DatasetsRoot = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const history = useHistory()
  const { product, project } = useStore()
  const dispatch = useDispatch()
  const { productId, projectId } = useParams<RouteParams>()
  const location = useLocation<LocationState>()
  const [selectedDatasetId, setSelectedDataset] = useState<number>()
  const [imagesCounter, setImagesCounter] = useState<number>(0)
  const [notesUpdate, setNotesUpdate] = useState<{
    datasetId: number
    notes: string
  } | null>(null)
  const guidedTourShown = useRef(false)

  const onObservationsCreated = (toAdd: number) => {
    fetchProduct(dispatch, Number(productId))
    setImagesCounter((prevState) => prevState + toAdd)
  }

  const handleRowClick = (dataset: Dataset) => {
    history.push({
      hash: dataset.id.toString(),
      state: { datasetId: dataset.id },
    })
  }

  const handleEditDataset = (dataset: { name: string; id: number }) => {
    // Navigate to AddDatasetPage with dataset id in URL
    history.push(
      locations.datasetEdit(Number(projectId), Number(productId), dataset.id)
    )
  }

  useEffect(() => {
    if (location.hash) {
      history.replace({
        hash: location.hash,
        state: {
          datasetId: Number(location.hash.substring(1)),
        },
      })
    }
  }, [history, location.hash])

  useEffect(() => {
    setSelectedDataset(location.state?.datasetId)
  }, [location.state])

  useEffect(() => {
    if (!project) {
      fetchProject(dispatch, Number(projectId))
    }
  }, [dispatch, project, projectId])

  useEffect(() => {
    fetchProduct(dispatch, Number(productId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { settings } = useContext(SettingsContext)
  const { updateSettings } = useUpdateSettings()

  const updatePreference = () => {
    const checkbox = document.getElementById(
      'doNotShowAgainCheckbox'
    ) as HTMLInputElement
    if (checkbox.checked === settings.preference__show_guided_tour) {
      updateSettings({
        preference__show_guided_tour: !checkbox.checked,
      })
    }
  }

  const classesDriver = useDriverStyle()

  const driverObj = getDriver(
    classesDriver,
    settings,
    updateSettings,
    updatePreference
  )

  useEffect(() => {
    if (guidedTourShown.current === false) {
      if (settings.preference__show_guided_tour && !driverObj.isActive()) {
        driverObj.drive()
        guidedTourShown.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.preference__show_guided_tour])

  return (
    <Content
      aside={<DatasetsAside />}
      asideWidth={vars.asideWidth}
      childrenClassName={classes.content}
      breadcrumbs={
        <Breadcrumbs
          elements={[
            <Link to={locations.projects}>{t('Projects')}</Link>,
            <Link to={locations.products(Number(projectId))}>
              {project?.name}
            </Link>,
            <Link
              to={locations.trainings(Number(projectId), Number(productId))}
            >
              {product?.name}
            </Link>,
            t('Datasets'),
          ]}
        />
      }
    >
      <div className={classes.datasets}>
        <div className={classes.list}>
          <PageHeader
            title={t('Datasets')}
            right={
              <div className={classes.flex}>
                <div id="newDatasetButtonId">
                  <Button
                    onClick={() =>
                      history.push(
                        locations.datasetNew(
                          Number(projectId),
                          Number(productId)
                        )
                      )
                    }
                    label={t('New dataset')}
                    buttonType="button"
                    type="action"
                    size="md"
                  />
                </div>
                <div className={classes.marginRightSm}>
                  <Button
                    label={t('Explain me how')}
                    hideLabel
                    icon={<FontAwesomeIcon icon={faCircleQuestion} size="lg" />}
                    type="blank-default"
                    onClick={() => {
                      driverObj.drive()
                    }}
                  />
                </div>
              </div>
            }
          />

          <DatasetsList
            selectedDatasetId={selectedDatasetId}
            productId={Number(productId)}
            projectId={Number(projectId)}
            onEditDataset={handleEditDataset}
            onRowClick={handleRowClick}
            notesUpdate={notesUpdate ?? undefined}
          />
        </div>

        {selectedDatasetId && (
          <div className={classes.sidePanel}>
            <DatasetDetails
              datasetId={selectedDatasetId}
              onNotesUpdated={(notes) =>
                setNotesUpdate({ datasetId: selectedDatasetId, notes })
              }
            />
          </div>
        )}
      </div>
    </Content>
  )
}

export default DatasetsRoot
